import React from "react";

/**
 * Iter39 — canonical AI-insight legal footnote. One component reused on:
 *   • ForecastStoryCard
 *   • Every Goals action-plan card
 *   • Variance insight cards
 *
 * Keeps the fine-print copy consistent, and the last-refreshed timestamp
 * rendered in a predictable UK format above it.
 *
 * Props:
 *   generatedAt   — ISO datetime string of the most recent generation
 *   showChecklist — if true, also render the goals-specific "tick steps off"
 *                   guidance line above the legal text (used on goal cards)
 *   allDone       — when showChecklist=true, tweaks the copy to acknowledge
 *                   completion vs still-in-progress state
 *   totalCount    — total number of action-plan steps (for the completion state)
 */
export default function AIInsightFootnote({
  generatedAt,
  showChecklist = false,
  allDone = false,
  totalCount = 0,
  className = "",
  "data-testid": dataTestid = "ai-insight-footnote",
}) {
  const stamp = generatedAt
    ? new Date(generatedAt).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className={`text-xs text-black/55 leading-relaxed space-y-2 ${className}`} data-testid={dataTestid}>
      {stamp && (
        <p className="font-mono text-[11px]">
          Last refreshed {stamp}
        </p>
      )}
      {showChecklist && (
        <p>
          Tick steps off as you complete them.{" "}
          {allDone && totalCount > 0
            ? "All steps ticked — regenerate for a fresh plan to move to the next stage."
            : "Once you've worked through this list, come back and regenerate a fresh plan to move to the next stage."}
        </p>
      )}
      <p className="border-t border-black/10 pt-2 italic">
        AI-generated insight — refreshes automatically when you log a weekly
        pulse, monthly actuals, a new goal or edit your forecast. This is not
        financial advice. Use this as a thinking partner, not a decision-maker
        — always sense-check against your own knowledge of the business. For
        material choices (hiring, borrowing, tax structure), please consult a
        qualified accountant or financial adviser.
      </p>
    </div>
  );
}
